import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { toast } from '../../../ui/toast';
import { PaginationController } from '../../../ui/pagination_controller/pagination_controller';
import { getJobList } from '../../../utils/api_service/job_api';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import {
  ContextBasedGeneratorJob,
  TopicBasedGeneratorJob,
} from '../../../utils/types/content_manager_types';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { DocumentTriage } from './document_triage/document_triage';
import { ContextSearch } from './search_bar';

export const ContentManager = () => {
  const [jobList, setJobList] = useState<
    (ContextBasedGeneratorJob | TopicBasedGeneratorJob)[]
      >([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const generateTypeList: SelectOptions[] = [
    {
      value: 'context',
      label: 'Context',
    },
    {
      value: 'topic',
      label: 'Topic',
    },
  ];

  const [generateType, setGenerateType] = useState<SelectOptions | null>(null);

  useEffect(() => {
    setPageNumber(1);
  }, [searchTerm, generateType]);

  useEffect(() => {
    setLoading(true);
    const debounceSearchTerm = setTimeout(() => {
      const fetchJobList = async () => {
        try {
          let generateTypeValue = null;
          if (generateType?.value !== 'None') {
            generateTypeValue = generateType?.value;
          }
          const response = await getJobList(
            pageNumber,
            10,
            searchTerm,
            generateTypeValue,
          );
          const totalPages = Math.ceil(response.totalJobs / 10);
          setJobList(response.jobs);
          setTotalPages(totalPages);
        } catch (error) {
          toast.error('Error Fetching Jobs ' + error, {
            position: 'bottom-center',
          });
          setJobList([]);
          setLoading(false);
        } finally {
          setLoading(false);
        }
      };
      fetchJobList();
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, generateType, pageNumber]);

  const handleSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectChange = (selectedValue: SingleValue<SelectOptions>) => {
    setGenerateType(selectedValue);
  };

  return (
    <div className='space-y-3'>
      <nav
          className='flex w-full flex-col gap-2 border-b border-slate-200 py-2 text-sm lg:flex-row lg:items-center lg:justify-between'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center gap-2'>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              Questions
            </Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          <li>
            <Link
                to={'#'}
                className='text-primary-main font-semibold hover:underline'
            >
              Content Manager
            </Link>
          </li>
        </ol>
        <div className='flex flex-wrap gap-2 text-xs font-semibold sm:text-sm'>
          <PermissionGate
              requiredPermissions={[Permission.QUESTIONS_VIEW]}
              matchAnyPermission={false}
          >
            <Link
                className='rounded-md px-2 py-1 text-primary-main hover:bg-emerald-50'
                to={'/question-bank'}
            >
              Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
              requiredPermissions={[Permission.QUESTIONS_VIEW]}
              matchAnyPermission={false}
          >
            <Link
                className='rounded-md px-2 py-1 text-primary-main hover:bg-emerald-50'
                to={'/pre-screen-bank'}
            >
              Pre Screen Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
              requiredPermissions={[Permission.JOB_CREATE]}
              matchAnyPermission={false}
          >
            <Link
                className='rounded-md px-2 py-1 text-primary-main hover:bg-emerald-50'
                to={'/upload-file'}
            >
              Auto Generate Questions
            </Link>
          </PermissionGate>
        </div>
      </nav>
      <div className='space-y-3'>
        <div className='rounded-lg border border-slate-200 bg-white p-2 shadow-sm'>
          <div className='flex flex-col gap-2 lg:flex-row lg:items-center'>
            <div className='min-w-0 flex-1'>
              <ContextSearch
                  searchTerm={searchTerm}
                  handleChange={handleSearchTermChange}
              />
            </div>
            <div className='w-full lg:w-72'>
              <Select
                  placeholder='Select Generation Type'
                  name='generationType'
                  options={generateTypeList}
                  onChange={handleSelectChange}
                  isClearable={true}
                  classNamePrefix='content-filter'
              />
            </div>
          </div>
        </div>
        <DocumentTriage
            loading={loading}
            documents={jobList}
            setDocuments={setJobList}
        />
        <div className='flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm'>
          <span className='text-xs font-semibold text-slate-500'>
            Page { pageNumber } of { totalPages || 1 }
          </span>
          <PaginationController
              pageNumber={pageNumber}
              maxPageNumber={totalPages}
              navigateNextPage={() =>
                setPageNumber((pageNumber) => pageNumber + 1)
              }
              navigatePreviousPage={() =>
                setPageNumber((pageNumber) => pageNumber - 1)
              }
          />
        </div>
      </div>
    </div>
  );
};
